/** One queue finding, collapsed and expandable (GIS Phase 2A, Task 10/11a,
 *  spec §6).
 *
 *  Collapsed: type badge, entity summary, a one-line values summary, age.
 *  Expanded: the lazy `EvidencePanel` (+ its `MiniMap`, spec §5) followed by
 *  the action buttons (the matrix, spec §3). Evidence fetches
 *  `GET /findings/{id}/evidence` only while `expanded` is true, and — same
 *  rule this component already enforced for its own `new_in_gis` summary
 *  line pre-Task-11a — NEVER for a `new_in_gis` finding (spec §5: the
 *  endpoint 422s on that type by design).
 *
 *  The evidence fetch uses `@tanstack/react-query`'s `useQuery` (`enabled:
 *  expanded && !isNewInGis`), matching `QueuePage`'s own idiom for the
 *  findings list rather than hand-rolling loading/error state in a raw
 *  effect (the equivalent `useEffect` + `useState` version tripped
 *  `react-hooks/set-state-in-effect` and duplicated what react-query
 *  already does correctly, incl. per-finding caching across a collapse/
 *  re-expand of the same row). `FindingRow.test.tsx`'s harness wraps in a
 *  fresh `QueryClientProvider` per render for this reason.
 *
 *  Candidate selection (relationship findings' evidence table / mini-map)
 *  lives here too, lifted above both `EvidencePanel` (which renders the
 *  table + map) and `ActionButtons` (which needs the selected id as
 *  `target_parent_id` for assign_parent/propose_parent) — see
 *  `ActionButtons.tsx`'s own docstring for the integration rationale. */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ActionButtons } from './ActionButtons.tsx'
import { EvidencePanel } from './EvidencePanel.tsx'
import { getFindingEvidence } from './api.ts'
import { DISCREPANCY_TYPE_LABELS, type DiscrepancyRow, type Finding, type FindingActionResponse } from './types.ts'

const TYPE_BADGE_STYLES: Record<string, string> = {
  attribute_changed: 'bg-yellow-100 text-yellow-700',
  relationship_conflict: 'bg-red-100 text-red-700',
  relationship_ambiguous: 'bg-orange-100 text-orange-700',
  relationship_unresolved: 'bg-orange-100 text-orange-700',
  identity_conflict: 'bg-purple-100 text-purple-700',
  missing_from_gis: 'bg-gray-200 text-gray-700',
  new_in_gis: 'bg-blue-100 text-blue-700',
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span
      className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
        TYPE_BADGE_STYLES[type] ?? 'bg-gray-100 text-gray-700'
      }`}
    >
      {DISCREPANCY_TYPE_LABELS[type as keyof typeof DISCREPANCY_TYPE_LABELS] ?? type}
    </span>
  )
}

/** Plain-language one-line summary of the finding's values (UX-review
 *  Stage-1: explicit outcomes, no jargon-only fields). */
function valueSummary(d: DiscrepancyRow): string {
  switch (d.discrepancy_type) {
    case 'attribute_changed':
      return `${d.field_name ?? 'value'}: ${d.platform_value ?? '(empty)'} → ${d.gis_value ?? '(empty)'}`
    case 'missing_from_gis':
      return `Not seen in the latest GIS import${d.gis_id ? ` (was ${d.gis_id})` : ''}`
    case 'new_in_gis':
      return `New in GIS: ${d.gis_value ?? d.gis_id ?? '(no value on the finding)'}`
    case 'identity_conflict':
      return `Incoming GIS identity ${d.gis_id ?? '(unknown)'} conflicts with the current owner`
    case 'relationship_conflict':
    case 'relationship_ambiguous':
    case 'relationship_unresolved':
      return `${d.field_name ?? 'parent'}: platform has ${d.platform_value ?? '(none)'}, GIS suggests ${
        d.gis_value ?? '(none)'
      }`
    default:
      return `${d.platform_value ?? '—'} → ${d.gis_value ?? '—'}`
  }
}

function ageLabel(ageDays: number): string {
  if (ageDays <= 0) return 'today'
  if (ageDays === 1) return '1 day old'
  return `${ageDays} days old`
}

interface FindingRowProps {
  finding: Finding
  capabilities: readonly string[]
  onActionSuccess: (finding: Finding, response: FindingActionResponse) => void
  onActionError: (message: string) => void
}

export function FindingRow({ finding, capabilities, onActionSuccess, onActionError }: FindingRowProps) {
  const [expanded, setExpanded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [selectedCandidateId, setSelectedCandidateId] = useState<number | null>(null)
  const d = finding.discrepancy
  const isNewInGis = d.discrepancy_type === 'new_in_gis'

  const evidenceQuery = useQuery({
    queryKey: ['gis-queue', 'evidence', d.id],
    queryFn: () => getFindingEvidence(d.id),
    enabled: expanded && !isNewInGis,
  })

  function toggleExpanded() {
    setExpanded(e => {
      const next = !e
      if (!next) {
        // Collapsing: drop the candidate selection so a later re-expand
        // starts without a leftover pick from a prior look at this same
        // finding. The evidence QUERY itself stays cached (react-query,
        // keyed on the finding id) so re-expanding the same row doesn't
        // re-fetch needlessly.
        setSelectedCandidateId(null)
      }
      return next
    })
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50"
      >
        <TypeBadge type={d.discrepancy_type} />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-gray-800 truncate">
            {finding.entity_summary ?? `${d.table_name} record ${d.record_id ?? '(none)'}`}
          </span>
          <span className="block text-xs text-gray-500 truncate">{valueSummary(d)}</span>
        </span>
        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">{ageLabel(finding.age_days)}</span>
        <span className="text-xs text-gray-400 shrink-0" aria-hidden="true">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-3 space-y-3">
          {isNewInGis && (
            <div className="text-xs text-gray-600">
              GIS value: <span className="font-mono">{d.gis_value ?? '—'}</span> — informational only,
              nothing to compare against.
            </div>
          )}
          {!isNewInGis && (
            <EvidencePanel
              findingId={d.id}
              evidence={evidenceQuery.data ?? null}
              isLoading={evidenceQuery.isLoading}
              error={evidenceQuery.error}
              selectedCandidateId={selectedCandidateId}
              onSelectCandidate={setSelectedCandidateId}
            />
          )}
          <ActionButtons
            finding={finding}
            capabilities={capabilities}
            busy={busy}
            onBusyChange={setBusy}
            selectedTargetParentId={selectedCandidateId}
            onSuccess={response => onActionSuccess(finding, response)}
            onError={onActionError}
          />
        </div>
      )}
    </div>
  )
}
